import { useCallback, useEffect, useRef, useState } from 'react'
import Image from '@talkdesk/cobalt-react-image'
import Box from '@talkdesk/cobalt-react-box'
import { useTheme } from '@talkdesk/cobalt-react-theme-provider'
import {
  DEFAULT_SIDE_ILLUSTRATION_PATH,
  SIDE_ILLUSTRATION,
} from '../../constants/images'
import { useTranslation } from 'react-i18next'

const Banner = ({ children }) => {
  const theme = useTheme()
  return (
    <Box
      backgroundColor={theme.gray200}
      height='100%'
      width='100%'
      style={{ overflowY: 'hidden' }}
    >
      {children}
    </Box>
  )
}

const MARKETING_RESOURCE_LOAD_EVENT = 'ResourceLoaded'

const SideIllustration = ({
  marketingBannerTimeout,
  marketingBannerURL,
  talkdeskIdAssetsURL,
}) => {
  const { t } = useTranslation()
  const [showPlaceholder, setShowPlaceholder] = useState(true)
  const [iframeLoaded, setIframeLoaded] = useState(false)
  const timerRef = useRef(null)

  const cleanup = useCallback(() => {
    clearTimeout(timerRef.current)
    timerRef.current = null
  }, [])

  const onIframeLoadEventReceived = useCallback(
    (event) => {
      if (event.data === MARKETING_RESOURCE_LOAD_EVENT && showPlaceholder) {
        cleanup()
        setIframeLoaded(true)
        setShowPlaceholder(false)
      }
    },
    [showPlaceholder, cleanup],
  )

  useEffect(() => {
    window.addEventListener('message', onIframeLoadEventReceived)
    return () => {
      window.removeEventListener('message', onIframeLoadEventReceived)
    }
  }, [onIframeLoadEventReceived])

  useEffect(() => {
    return () => cleanup()
  }, [cleanup])

  const onIframeLoad = () => {
    if (!iframeLoaded) {
      timerRef.current = setTimeout(() => {
        cleanup()
        setShowPlaceholder(false)
      }, marketingBannerTimeout)
    }
  }

  const getIframeStyle = () => {
    const showIframe = !showPlaceholder && iframeLoaded
    const opacity = showIframe
      ? { opacity: 1, transition: 'opacity 500ms' }
      : { opacity: 0 }
    const percentage = showIframe ? '100%' : '0%'
    const dimensions = { width: percentage, height: percentage }
    return { ...dimensions, ...opacity, border: 'none' }
  }

  const renderIframe = () => (
    <iframe
      title={t('login.marketingResourceTitle')}
      src={marketingBannerURL}
      // eslint-disable-next-line @eslint-react/dom-no-unsafe-iframe-sandbox -- marketingBannerURL is a fixed, first-party Talkdesk CDN (cdn.talkdesk.marketing), not third-party/tenant-controlled content
      sandbox='allow-scripts allow-same-origin'
      style={getIframeStyle()}
      onLoad={onIframeLoad}
    />
  )

  const renderImage = (
    path = DEFAULT_SIDE_ILLUSTRATION_PATH,
    title = t('login.sideIllustrationTitle'),
  ) => {
    const imageLocation = SIDE_ILLUSTRATION(talkdeskIdAssetsURL, path)
    return (
      <Image
        src={imageLocation}
        alt={title}
        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
      />
    )
  }

  const renderContent =
    !showPlaceholder && !iframeLoaded ? renderImage : renderIframe

  return <Banner>{renderContent()}</Banner>
}

export default SideIllustration
